import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import AppDialog, { AppDialogTitle, AppDialogContent, AppDialogActions } from '../layout/AppDialog';
import { updateOrder, resendOtp, updateOrderStatus, getOrderSmsStatus, resendOrderReceivedSms, manualDeliveryOverride } from '../../services/api';

const EditOrderModal = ({ open, onClose, onOrderUpdated, order }) => {
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [originalOrderNo, setOriginalOrderNo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isDispatching, setIsDispatching] = useState(false);
  const [smsStatus, setSmsStatus] = useState(null);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  
  const [isManualDelivering, setIsManualDelivering] = useState(false);
  const [receiverName, setReceiverName] = useState('');
  const [showManualDeliveryForm, setShowManualDeliveryForm] = useState(false);
  
  const userRole = localStorage.getItem('userRole');
  const normalizedUserRole = (userRole || '').toLowerCase();

  const fetchSmsStatus = useCallback(async () => {
    if (!order) return;
    try {
      const response = await getOrderSmsStatus(order.id);
      setSmsStatus(response.data);
    } catch (error) {
    }
  }, [order]);

  useEffect(() => {
    if (order) {
      
      
      setCustomerName(order.customer_name || '');
      setCustomerPhone(order.customer_phone || '');
      setOrderNo(order.order_no || '');
      setOriginalOrderNo(order.order_no || '');
      setCustomerAddress(order.customer_address || '');
      // Fetch SMS status when order is loaded
      fetchSmsStatus();
    }
  }, [order, fetchSmsStatus, userRole]);

  // Check if order number changed and resend SMS if needed
  const checkOrderNumberChange = async () => {
    const currentOrderNo = orderNo.trim() || null;
    const originalOrderNoValue = originalOrderNo || null;
    
    // Check if order number actually changed
    if (currentOrderNo !== originalOrderNoValue) {
      try {
        setProcessingMessage('Order number changed. Resending order received SMS...');
        setShowProcessingModal(true);
        
        const response = await resendOrderReceivedSms(order.id);
        
        if (response.data?.status === 'sms_sent' || response.data?.status === 'sms_delivered') {
          setProcessingMessage('Order received SMS sent successfully!');
          setTimeout(() => {
            setShowProcessingModal(false);
            setProcessingMessage('');
          }, 2000);
        } else {
          setProcessingMessage('SMS sent but with warnings. Check SMS status.');
          setTimeout(() => {
            setShowProcessingModal(false);
            setProcessingMessage('');
          }, 3000);
        }
        
        // Refresh SMS status
        await fetchSmsStatus();
        
      } catch (error) {
        setProcessingMessage(`Failed to resend SMS: ${error.response?.data?.message || error.message}`);
        setTimeout(() => {
          setShowProcessingModal(false);
          setProcessingMessage('');
        }, 3000);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Check if order is in editable state based on user role
    const canEdit = (normalizedUserRole === 'clerk' && order?.status_id === 1) || 
                   (normalizedUserRole === 'cus-admin' && (order?.status_id === 1 || order?.status_id === 2)) ||
                   (normalizedUserRole === 'super-admin');
    
    if (!order || !canEdit) {
      const statusText = order?.status_id === 1 ? 'Received' : 
                        order?.status_id === 2 ? 'Dispatched' : 
                        order?.status_id === 3 ? 'Delivered' : 
                        order?.status_id === 4 ? 'Pending SMS Confirmation' : 'Unknown';
      
      if (normalizedUserRole === 'cus-admin') {
        alert(`Order cannot be edited. Current status: ${statusText}. Customer admins can only edit orders with 'Received' or 'Dispatched' status.`);
      } else {
        alert(`Order cannot be edited. Current status: ${statusText}. Only orders with 'Received' status can be edited.`);
      }
      return;
    }

    // Basic validation
    if (!customerName.trim()) {
      alert('Customer name is required');
      return;
    }
    if (!customerPhone.trim()) {
      alert('Customer phone is required');
      return;
    }
    if (!customerAddress.trim()) {
      alert('Customer address is required');
      return;
    }

    setIsSubmitting(true);
    try {

      const response = await updateOrder(order.id, {
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        customer_address: customerAddress.trim(),
        order_no: orderNo.trim() || null, // Send null if empty
      });
      
      
      // Check if the update was successful
      if (response && response.status >= 200 && response.status < 300) {
        // Check if order number changed and resend SMS if needed
        await checkOrderNumberChange();
        
        if (onOrderUpdated) {
          onOrderUpdated();
        }
        onClose();
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      
      let errorMessage = 'Error updating order';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`Error updating order: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendOtp = async () => {
    if (isSendingOtp) return;
    
    setIsSendingOtp(true);
    try {
      const response = await resendOtp(order.id);
      
      // Check if there's a warning about SMS not being sent
      if (response.data?.warning) {
        alert(`Warning: ${response.data.message}`);
      } else {
        alert(`OTP has been sent successfully for order with ${order.order_no ? `order number ${order.order_no}` : `waybill number ${order.waybill_no}`}`);
      }
      
      // Refresh SMS status
      await fetchSmsStatus();
      
      // Refresh the parent component
      if (onOrderUpdated) {
        onOrderUpdated();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error sending OTP';
      alert(`Error sending OTP: ${errorMessage}`);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleDispatch = async () => {
    if (isDispatching) return;
    
    // Check if SMS has failed - but allow admin, cus-admin, and clerk to dispatch anyway
    if (smsStatus && smsStatus.has_sms && smsStatus.sms_status === 'Failed' &&
        normalizedUserRole !== 'cus-admin' && normalizedUserRole !== 'clerk') {
      alert('Cannot dispatch order with failed SMS. Please resend OTP first.');
      return;
    }
    
    setIsDispatching(true);
    setProcessingMessage('Dispatching order and sending OTP...');
    setShowProcessingModal(true);
    
    try {
      const response = await updateOrderStatus(order.id, 'dispatched');
      
      // Check if there's a warning about SMS not being sent
      if (response.data?.warning) {
        setProcessingMessage(`Warning: ${response.data.message}`);
        setTimeout(() => {
          setShowProcessingModal(false);
          setProcessingMessage('');
        }, 3000);
      } else {
        setProcessingMessage('Order dispatched successfully! OTP sent to customer.');
        setTimeout(() => {
          setShowProcessingModal(false);
          setProcessingMessage('');
          onClose();
        }, 2000);
      }
      
      // Refresh SMS status
      await fetchSmsStatus();
      
      // Refresh the parent component
      if (onOrderUpdated) {
        onOrderUpdated();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error dispatching order';
      setProcessingMessage(`Failed to dispatch order: ${errorMessage}`);
      setTimeout(() => {
        setShowProcessingModal(false);
        setProcessingMessage('');
      }, 3000);
    } finally {
      setIsDispatching(false);
    }
  };

  const handleManualDelivery = async () => {
    if (isManualDelivering) return;
    
    setIsManualDelivering(true);
    setProcessingMessage('Manually marking order as delivered...');
    setShowProcessingModal(true);
    
    try {
      await manualDeliveryOverride(order.id, receiverName);
      
      setProcessingMessage('Order manually marked as delivered successfully!');
      setTimeout(() => {
        setShowProcessingModal(false);
        setProcessingMessage('');
        setShowManualDeliveryForm(false);
        setReceiverName('');
        onClose();
      }, 2000);
      
      // Refresh the parent component
      if (onOrderUpdated) {
        onOrderUpdated();
      }
    } catch (error) {
      const errorData = error.response?.data || {};
      const errorCode = errorData.error || 'UNKNOWN_ERROR';
      const baseMessage = errorData.message || error.message || 'Error marking order as delivered';
      
      let detailedMessage = `Failed to mark order as delivered: ${baseMessage}`;
      
      // Add specific error details based on error type
      if (errorCode === 'INVALID_ORDER_STATUS') {
        detailedMessage += `\n\nCurrent Status: ${errorData.currentStatusName || 'Unknown'}\nRequired Status: ${errorData.allowedStatusNames?.join(' or ') || 'Received or Dispatched'}`;
      } else if (errorCode === 'SMS_NOT_FAILED') {
        detailedMessage += `\n\nSMS Status: ${errorData.smsStatus || 'Unknown'}\nSMS Code: ${errorData.smsStatusCode || 'N/A'}\nReason: ${errorData.failureReason || 'No failure reason provided'}`;
      } else if (errorCode === 'STATUS_CONFIG_ERROR') {
        detailedMessage += `\n\nDetails: ${errorData.details || 'System configuration issue'}`;
      } else if (errorData.details) {
        detailedMessage += `\n\nDetails: ${errorData.details}`;
      }
      
      setProcessingMessage(detailedMessage);
      setTimeout(() => {
        setShowProcessingModal(false);
        setProcessingMessage('');
      }, 5000); // Increased timeout for longer error messages
    } finally {
      setIsManualDelivering(false);
    }
  };

  // Check if Dispatch button should be shown (only for received orders)
  const shouldShowDispatch = order && order.status_id === 1;
  
  // Check if Send OTP button should be shown (for dispatched orders or pending SMS confirmation)
  const shouldShowSendOtp = order && (order.status_id === 2 || order.status_id === 4);

  // Check if Manual Delivery button should be shown (for cus-admin with pending or dispatched orders)
  const shouldShowManualDelivery = normalizedUserRole === 'cus-admin' && order && (order.status_id === 1 || order.status_id === 2);
  
  // Check if manual delivery should be disabled (only for dispatched orders)
  const isManualDeliveryDisabled = !order || order.status_id !== 2;

  // Check if dispatch should be disabled (SMS failed) - but allow admin, cus-admin, and clerk to dispatch anyway
    const isDispatchDisabled = smsStatus && smsStatus.has_sms && smsStatus.sms_status === 'Failed' &&
                              normalizedUserRole !== 'cus-admin' && normalizedUserRole !== 'clerk';
  
  // Debug logging for dispatch button state

  return (
    <>
      <AppDialog open={open} onClose={onClose}>
        <AppDialogTitle onClose={onClose} subtitle="Update order status and delivery details">
          Edit Order
        </AppDialogTitle>
        <form onSubmit={handleSubmit}>
          <AppDialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Order No (Optional)"
                variant="outlined"
                fullWidth
                value={orderNo}
                onChange={(e) => setOrderNo(e.target.value)}
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderColor: (orderNo.trim() || null) !== (originalOrderNo || null) ? '#1976d2' : undefined,
                    '&:hover': {
                      borderColor: (orderNo.trim() || null) !== (originalOrderNo || null) ? '#1976d2' : undefined,
                    }
                  }
                }}
                disabled={isSubmitting}
                helperText={(orderNo.trim() || null) !== (originalOrderNo || null) ? 'Order number changed - SMS will be resent automatically' : ''}
                FormHelperTextProps={{
                  sx: { color: '#1976d2', fontSize: '0.75rem' }
                }}
              />
              <TextField
                label="Customer Name"
                variant="outlined"
                fullWidth
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                sx={{ mb: 3 }}
                disabled={isSubmitting}
              />
              <TextField
                label="Phone Number"
                variant="outlined"
                fullWidth
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                sx={{ mb: 3 }}
                disabled={isSubmitting}
              />
              <TextField
                label="Address"
                variant="outlined"
                fullWidth
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
                disabled={isSubmitting}
              />
            </Box>
          </AppDialogContent>
          <AppDialogActions sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button onClick={onClose} variant="outlined" color="inherit" disabled={isSubmitting}>
              Cancel
            </Button>
            
            {/* Dispatch Button - shown for received orders */}
            {shouldShowDispatch && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleDispatch}
                disabled={isDispatching || isSubmitting || isDispatchDisabled}
                startIcon={isDispatching ? <CircularProgress size={20} /> : null}
            title={smsStatus && smsStatus.has_sms && smsStatus.sms_status === 'Failed' && (normalizedUserRole === 'cus-admin' || normalizedUserRole === 'clerk')
              ? 'SMS delivery failed, but cus-admin/clerk can still dispatch this order'
              : ''}
          >
            {isDispatching ? 'Dispatching...' :
             (smsStatus && smsStatus.has_sms && smsStatus.sms_status === 'Failed' && (normalizedUserRole === 'cus-admin' || normalizedUserRole === 'clerk')
              ? 'Dispatch (SMS Failed)'
              : 'Dispatch')}
              </Button>
            )}
            
            {/* Send OTP Button - shown for dispatched orders or pending SMS confirmation */}
            {shouldShowSendOtp && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSendOtp}
                disabled={isSendingOtp || isSubmitting}
                startIcon={isSendingOtp ? <CircularProgress size={20} /> : null}
              >
                {isSendingOtp ? 'Sending OTP...' : (order.status_id === 4 ? 'Resend OTP' : 'Send OTP')}
              </Button>
            )}
            
            {/* Manual Delivery Button - shown for cus-admin with pending or dispatched orders */}
            {shouldShowManualDelivery && (
              <Button
                variant="contained"
                color="success"
                onClick={() => setShowManualDeliveryForm(true)}
                disabled={isManualDelivering || isSubmitting || isManualDeliveryDisabled}
                startIcon={isManualDelivering ? <CircularProgress size={20} /> : null}
                title={isManualDeliveryDisabled ? 'Order must be dispatched before manual delivery' : ''}
              >
                {isManualDelivering ? 'Processing...' : 
                 isManualDeliveryDisabled ? 'Mark as Delivered (Dispatch First)' : 'Mark as Delivered'}
              </Button>
            )}
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? 'Updating Order...' : 'Update Order'}
            </Button>
          </AppDialogActions>
        </form>
      </AppDialog>

      {/* Manual Delivery Confirmation Dialog */}
      <AppDialog open={showManualDeliveryForm} onClose={() => setShowManualDeliveryForm(false)}>
        <AppDialogTitle
          onClose={() => setShowManualDeliveryForm(false)}
          subtitle="Record delivery when OTP verification is unavailable"
        >
          Manual Delivery Override
        </AppDialogTitle>
        <AppDialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to manually mark this order as delivered. This will bypass the normal OTP verification process.
          </Typography>
          <TextField
            fullWidth
            label="Receiver Name (Optional)"
            value={receiverName}
            onChange={(e) => setReceiverName(e.target.value)}
            placeholder="Enter receiver name or leave blank"
            margin="normal"
          />
        </AppDialogContent>
        <AppDialogActions>
          <Button onClick={() => setShowManualDeliveryForm(false)} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleManualDelivery}
            variant="contained"
            color="success"
            disabled={isManualDelivering}
            startIcon={isManualDelivering ? <CircularProgress size={20} /> : null}
          >
            {isManualDelivering ? 'Processing...' : 'Confirm Delivery'}
          </Button>
        </AppDialogActions>
      </AppDialog>

      {/* Processing Modal */}
      <AppDialog open={showProcessingModal} onClose={() => {}} disableEscapeKeyDown>
        <AppDialogTitle align="center" showClose={false}>
          Processing...
        </AppDialogTitle>
        <AppDialogContent dividers={false} sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="body1">
            {processingMessage}
          </Typography>
        </AppDialogContent>
      </AppDialog>
    </>
  );
};

export default EditOrderModal; 